/**
 * EmailCompose — compose drawer rendered as a Preact overlay in the reading pane.
 *
 * For new compose, it opens as a full reading-pane-width form.
 * For reply/forward, it opens as a sticky form at the bottom of the reading pane.
 *
 * TinyMCE is initialised via the email-tinymce.js module after the textarea mounts.
 * Autosave uses the email-autosave.js module — saves every 5s if content changed.
 * When compose is opened from a draft card, composeDraftData pre-fills the form.
 *
 * The form is a regular HTML form that posts to the Rails controller (no Preact state
 * for form field values — that would conflict with TinyMCE / Tom Select). Only the
 * visibility and metadata (composeType, draftId) live in the store.
 */

import { h } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { post } from '@rails/request.js'
import { initTinyMCE, removeTinyMCE, syncTinyMCE } from '../../modules/email/email-tinymce.js'
import { EmailAutosave } from '../../modules/email/email-autosave.js'

export function EmailCompose({ store, i18n, mailboxes = [] }) {
  const composeOpen  = store.composeOpen.value
  const composeType  = store.composeType.value
  const draftId      = store.draftId.value
  const originalId   = store.composeOriginalEmailId.value
  const draftData    = store.composeDraftData.value

  if (!composeOpen) return null

  return (
    <ComposeForm
      key={`${composeType}-${originalId ?? 'new'}`}
      composeType={composeType}
      draftId={draftId}
      originalId={originalId}
      draftData={draftData}
      store={store}
      i18n={i18n}
      mailboxes={mailboxes}
    />
  )
}

const isMobile = () => window.innerWidth < 768

function ComposeForm({ composeType, draftId, originalId, draftData, store, i18n, mailboxes }) {
  const editorId  = `email_compose_editor_${composeType}_${originalId || 'new'}`
  const editorRef = useRef(null)
  const formRef   = useRef(null)
  const paneRef   = useRef(null)
  const autosaveRef = useRef(null)

  const [showCc,  setShowCc]  = useState(!!(draftData?.cc_addresses?.length))
  const [showBcc, setShowBcc] = useState(false)
  const [subject, setSubject] = useState(draftData?.subject || '')
  const [draftIndicator, setDraftIndicator] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState('')

  const closeBtnRef = useRef(null)
  const discardBtnRef = useRef(null)

  const isReply   = ['reply', 'reply_all', 'forward'].includes(composeType)
  const formUrl   = isReply ? '/emails/compose/send_reply' : '/emails/compose'
  const mobile    = isMobile()

  // On mobile: compute TinyMCE height so editor fills remaining space
  function _calcMobileTinyHeight() {
    const pane = paneRef.current
    if (!pane || !mobile) return mobile ? 200 : 320
    const totalH  = window.innerHeight
    const usedH   = [...pane.querySelectorAll(':scope > .d-flex, :scope > form > .d-flex, :scope > form > .border-bottom')]
      .filter(el => !el.classList.contains('flex-grow-1'))
      .reduce((sum, el) => sum + (el.offsetHeight || 0), 0)
    const headerH = pane.querySelector('.d-flex.align-items-center.border-bottom')?.offsetHeight || 48
    const footerH = pane.querySelector('.border-top.bg-body-tertiary')?.offsetHeight || 48
    const fieldH  = pane.querySelectorAll('form > .d-flex.align-items-center, form > .border-bottom')
    let fields = 0
    fieldH.forEach(el => { fields += el.offsetHeight || 0 })
    const remaining = totalH - headerH - fields - footerH - 24
    return Math.max(120, remaining)
  }

  // Init TinyMCE after mount, set up autosave and native button listeners
  useEffect(() => {
    if (!editorRef.current) return

    const initialBody = draftData?.body_html || ''
    const editorHeight = mobile
      ? Math.max(120, window.innerHeight - 250)
      : (isReply ? 200 : 320)

    initTinyMCE(`#${editorId}`, {
      height: editorHeight,
      placeholder: i18n?.t('emails.reply_placeholder'),
      initialContent: initialBody,
      onInput: () => { store.markComposeDirty(); autosaveRef.current?.notifyChange() },
    }).then(() => {
      // Recalculate after TinyMCE renders (DOM may have shifted)
      if (mobile) {
        requestAnimationFrame(() => {
          const tox = paneRef.current?.querySelector('.tox-tinymce')
          if (tox) {
            const h = _calcMobileTinyHeight()
            tox.style.height = `${h}px`
          }
        })
      }
    })

    // Autosave: build JSON payload from form state + TinyMCE content
    autosaveRef.current = new EmailAutosave({
      store,
      getPayload: () => {
        syncTinyMCE()
        const form = formRef.current
        if (!form) return null
        const bodyHtml = form.querySelector(`textarea#${editorId}`)?.value || ''
        const toRaw    = form.querySelector('[name="_to_addresses_raw"]')?.value || ''
        const ccRaw    = form.querySelector('[name="_cc_addresses_raw"]')?.value || ''
        const bccRaw   = form.querySelector('[name="_bcc_addresses_raw"]')?.value || ''
        const subjectV = form.querySelector('[name="email[subject]"]')?.value || ''
        const mailboxId = form.querySelector('[name="email[mailbox_id]"]')?.value || null
        return {
          email_draft: {
            draft_type:       composeType === 'reply_all' ? 'reply' : (composeType || 'new'),
            body:             bodyHtml,
            subject:          subjectV,
            mailbox_id:       mailboxId,
            reply_to_email_id: originalId || null,
            to_addresses:     toRaw ? toRaw.split(',').map(s => s.trim()).filter(Boolean) : [],
            cc_addresses:     ccRaw ? ccRaw.split(',').map(s => s.trim()).filter(Boolean) : [],
            bcc_addresses:    bccRaw ? bccRaw.split(',').map(s => s.trim()).filter(Boolean) : [],
          }
        }
      },
      onSaved: () => {
        const now = new Date()
        const hh  = String(now.getHours()).padStart(2, '0')
        const mm  = String(now.getMinutes()).padStart(2, '0')
        setDraftIndicator(
          i18n?.t('emails.draft_saved') + ` ${hh}:${mm}`
        )
      },
      onCreated: (_id) => {
        setDraftIndicator(i18n?.t('emails.draft_saved'))
      },
    })
    autosaveRef.current.start()

    // Native event listeners — TinyMCE DOM mutations can break Preact's vDOM ↔ DOM sync
    function onClose() {
      // X button: keep the draft, just close compose and refresh thread
      autosaveRef.current?.saveNow()
      autosaveRef.current?.stop()
      store.closeCompose()
      // Refresh thread so draft card appears
      if (originalId) store.fetchThread(originalId)
    }

    function onDiscard() {
      // Trash button: confirm if dirty, then delete draft and close
      if (store.composeDirty.value || store.draftId.value) {
        if (!confirm(i18n?.t('emails.discard_confirm'))) return
      }
      autosaveRef.current?.deleteDraft()
      autosaveRef.current?.stop()
      store.closeCompose()
      // Refresh thread so deleted draft card disappears
      if (originalId) store.fetchThread(originalId)
    }
    const closeBtnEl   = closeBtnRef.current
    const discardBtnEl = discardBtnRef.current
    closeBtnEl?.addEventListener('click', onClose)
    discardBtnEl?.addEventListener('click', onDiscard)

    return () => {
      autosaveRef.current?.stop()
      removeTinyMCE(`#${editorId}`)
      closeBtnEl?.removeEventListener('click', onClose)
      discardBtnEl?.removeEventListener('click', onDiscard)
    }
  }, [])

  async function handleSubmit(e) {
    e.preventDefault()
    if (sending) return
    setSending(true)

    syncTinyMCE()
    const form = formRef.current

    // Build FormData, converting _*_addresses_raw → email[to/cc/bcc_addresses][]
    const fd = new FormData(form)
    const toRaw  = fd.get('_to_addresses_raw')  || ''
    const ccRaw  = fd.get('_cc_addresses_raw')  || ''
    const bccRaw = fd.get('_bcc_addresses_raw') || ''
    fd.delete('_to_addresses_raw')
    fd.delete('_cc_addresses_raw')
    fd.delete('_bcc_addresses_raw')
    toRaw.split(',').map(s => s.trim()).filter(Boolean).forEach(a => fd.append('email[to_addresses][]', a))
    ccRaw.split(',').map(s => s.trim()).filter(Boolean).forEach(a => fd.append('email[cc_addresses][]', a))
    bccRaw.split(',').map(s => s.trim()).filter(Boolean).forEach(a => fd.append('email[bcc_addresses][]', a))

    try {
      const res = await post(formUrl, { body: fd, responseKind: 'json' })
      if (res.ok) {
        // Delete the draft if one was autosaved
        await autosaveRef.current?.deleteDraft()
        autosaveRef.current?.stop()
        store.closeCompose()
        store.fetchEmails(1)
        // Reload the thread to remove draft card and show sent message
        if (originalId) store.fetchThread(originalId)
      } else {
        setSending(false)
        setSendError(i18n?.t('emails.send_error') || 'A levél küldése nem sikerült. Kérjük, próbálja újra.')
        console.error('[EmailCompose] send failed', res.statusCode)
      }
    } catch (err) {
      setSending(false)
      setSendError(i18n?.t('emails.send_error') || 'A levél küldése nem sikerült. Kérjük, próbálja újra.')
      console.error('[EmailCompose] send error', err)
    }
  }

  const title = composeType === 'forward' ? i18n?.t('emails.forward')
              : composeType === 'reply_all' ? i18n?.t('emails.reply_all')
              : composeType === 'reply' ? i18n?.t('emails.reply')
              : i18n?.t('emails.compose')

  function handleDragOver(e) {
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'copy'
    }
  }

  function handleDrop(e) {
    if (!e.dataTransfer.files?.length) return
    e.preventDefault()
    const input = formRef.current?.querySelector('input[type="file"][name="email[files][]"]')
    if (!input) return
    // Build a new FileList-compatible DataTransfer and assign to the file input
    const dt = new DataTransfer()
    ;[...(input.files || []), ...e.dataTransfer.files].forEach(f => dt.items.add(f))
    input.files = dt.files
    store.markComposeDirty()
  }

  return (
    <div
      ref={paneRef}
      id="email_compose_pane"
      class="d-flex flex-column email-compose-pane"
      style={mobile ? 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:1060;background:var(--bs-body-bg);height:100dvh' : 'height:100%'}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div class="d-flex align-items-center px-3 py-2 border-bottom bg-body-tertiary flex-shrink-0">
        <h6 class="mb-0 fw-semibold flex-grow-1">
          <i class={`bi ${composeType === 'new' ? 'bi-pencil-square' : 'bi-reply'} me-1`} />
          {title}
        </h6>
        {draftIndicator && (
          <span class="text-muted small me-2 d-flex align-items-center gap-1">
            <i class="bi bi-check2 text-success" />
            {draftIndicator}
          </span>
        )}
        <button ref={closeBtnRef} type="button" class="btn btn-sm btn-link text-muted p-0" title={i18n?.t('emails.discard')}>
          <i class="bi bi-x-lg" />
        </button>
      </div>

      {/* Send error alert */}
      {sendError && (
        <div class="alert alert-danger alert-dismissible mx-3 mt-2 py-2 small mb-0" role="alert">
          <i class="bi bi-exclamation-triangle-fill me-1" />
          {sendError}
          <button type="button" class="btn-close btn-sm p-2" onClick={() => setSendError('')} aria-label="Close" />
        </div>
      )}

      <form
        ref={formRef}
        onSubmit={handleSubmit}
        encType="multipart/form-data"
        class="d-flex flex-column flex-grow-1 overflow-hidden"
      >
        {originalId && <input type="hidden" name="email_id" value={originalId} />}
        {originalId && <input type="hidden" name="reply_mode" value={composeType} />}
        {draftId    && <input type="hidden" name="draft_id" value={draftId} />}

        {/* From / mailbox selector */}
        {mailboxes.length > 0 && (
          <div class="d-flex align-items-center border-bottom px-3 py-1">
            <span class="text-muted small email-field-label me-2">{i18n?.t('emails.from')}</span>
            <select name="email[mailbox_id]" class="form-select form-select-sm border-0 shadow-none ps-0 flex-grow-1">
              {mailboxes.map(mb => (
                <option key={mb.id} value={mb.id} selected={draftData?.mailbox_id === mb.id}>{mb.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* To */}
        <div class="d-flex align-items-center border-bottom px-3 py-1">
          <span class="text-muted small email-field-label me-2">{i18n?.t('emails.to')}</span>
          <input
            type="text"
            name="_to_addresses_raw"
            class="form-control border-0 shadow-none flex-grow-1"
            placeholder={i18n?.t('emails.to_placeholder')}
            defaultValue={(draftData?.to_addresses || []).join(', ')}
            onInput={() => { store.markComposeDirty(); autosaveRef.current?.notifyChange() }}
          />
          <button
            type="button"
            class="btn btn-sm btn-link text-muted small p-0 flex-shrink-0"
            onClick={() => { setShowCc(!showCc); setShowBcc(!showBcc) }}
          >
            Cc/Bcc
          </button>
        </div>

        {/* CC */}
        {showCc && (
          <div class="d-flex align-items-center border-bottom px-3 py-1">
            <span class="text-muted small email-field-label me-2">{i18n?.t('emails.cc')}</span>
            <input
              type="text"
              name="_cc_addresses_raw"
              class="form-control border-0 shadow-none flex-grow-1"
              defaultValue={(draftData?.cc_addresses || []).join(', ')}
            />
          </div>
        )}

        {/* BCC */}
        {showBcc && (
          <div class="d-flex align-items-center border-bottom px-3 py-1">
            <span class="text-muted small email-field-label me-2">{i18n?.t('emails.bcc')}</span>
            <input type="text" name="_bcc_addresses_raw" class="form-control border-0 shadow-none flex-grow-1" />
          </div>
        )}

        {/* Subject */}
        <div class="border-bottom px-3 py-1">
          <input
            type="text"
            name="email[subject]"
            class="form-control border-0 shadow-none px-0 fw-medium"
            placeholder={i18n?.t('emails.subject_placeholder')}
            value={subject}
            onInput={e => { setSubject(e.target.value); store.markComposeDirty(); autosaveRef.current?.notifyChange() }}
          />
        </div>

        {/* TinyMCE body */}
        <div class={mobile ? 'px-3 py-2 flex-shrink-0' : 'flex-grow-1 px-3 py-2 overflow-y-auto'}>
          <textarea
            ref={editorRef}
            id={editorId}
            name="email[body_html]"
            class="tinymce"
            rows={8}
            style="width:100%"
            onInput={() => store.markComposeDirty()}
          />
        </div>

        {/* Footer */}
        <div class="border-top px-3 py-2 d-flex align-items-center gap-2 flex-shrink-0 bg-body-tertiary">
          <label class="btn btn-sm btn-outline-secondary mb-0" title={i18n?.t('emails.attach_file')}>
            <i class="bi bi-paperclip" />
            <input type="file" name="email[files][]" multiple class="d-none" />
          </label>

          <button
            ref={discardBtnRef}
            type="button"
            class="btn btn-sm btn-link text-danger p-0"
            title={i18n?.t('emails.discard')}
          >
            <i class="bi bi-trash3" />
          </button>

          <button
            type="submit"
            class="btn btn-primary btn-sm d-flex align-items-center gap-1 ms-auto"
            disabled={sending}
          >
            {sending
              ? <span class="spinner-border spinner-border-sm me-1" role="status" />
              : <i class="bi bi-send" />
            }
            {i18n?.t('emails.send')}
          </button>
        </div>

      </form>
    </div>
  )
}
